import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type PaginationItem = number | "...";

export class Pagination implements Plugin {
  private currentPage: number = 1;
  private totalPages: number = 1;

  private readonly prevBtn: HTMLButtonElement | null;
  private readonly nextBtn: HTMLButtonElement | null;
  private readonly pagesContainer: HTMLElement | null;

  constructor(private readonly element: HTMLElement) {
    this.currentPage = parseInt(element.dataset.currentPage || "1", 10);
    this.totalPages = parseInt(element.dataset.totalPages || "1", 10);

    this.prevBtn = element.querySelector(".core-pagination__link--prev");
    this.nextBtn = element.querySelector(".core-pagination__link--next");
    this.pagesContainer = element.querySelector(".core-pagination__pages");
  }

  init(): void {
    if (!this.pagesContainer) return;

    this.render();
    this.setupEvents();
  }

  private setupEvents(): void {
    this.prevBtn?.addEventListener("click", () => {
      if (this.currentPage > 1) {
        this.selectPage(this.currentPage - 1);
      }
    });

    this.nextBtn?.addEventListener("click", () => {
      if (this.currentPage < this.totalPages) {
        this.selectPage(this.currentPage + 1);
      }
    });

    this.pagesContainer?.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const button = target.closest(
        ".core-pagination__page-button",
      ) as HTMLElement;

      if (button) {
        const page = parseInt(button.dataset.page || "1", 10);
        this.selectPage(page);
      }
    });
  }

  private selectPage(page: number): void {
    if (page === this.currentPage) return;

    this.currentPage = page;
    this.render();

    this.element.dispatchEvent(
      new CustomEvent("pageChange", {
        detail: { page: this.currentPage },
        bubbles: true,
      }),
    );
  }

  private render(): void {
    if (!this.pagesContainer) return;

    this.updateNavigationButtons();

    const items = this.calculatePaginationItems();

    this.pagesContainer.innerHTML = items
      .map((item) => this.createPageHTML(item))
      .join("");
  }

  private updateNavigationButtons(): void {
    if (this.prevBtn) {
      const isDisabled = this.currentPage === 1;
      this.toggleButtonState(this.prevBtn, isDisabled);
    }
    if (this.nextBtn) {
      const isDisabled = this.currentPage === this.totalPages;
      this.toggleButtonState(this.nextBtn, isDisabled);
    }
  }

  private toggleButtonState(btn: HTMLButtonElement, disabled: boolean) {
    if (disabled) {
      btn.classList.replace(
        "core-pagination__link--enabled",
        "core-pagination__link--disabled",
      );
      btn.setAttribute("aria-disabled", "true");
      btn
        .querySelector(".core-pagination__icon")
        ?.classList.replace(
          "core-pagination__icon--state-enabled",
          "core-pagination__icon--state-disabled",
        );
      btn
        .querySelector(".core-pagination__link-label")
        ?.classList.replace(
          "core-pagination__link-label--enabled",
          "core-pagination__link-label--disabled",
        );
    } else {
      btn.classList.replace(
        "core-pagination__link--disabled",
        "core-pagination__link--enabled",
      );
      btn.setAttribute("aria-disabled", "false");
      btn
        .querySelector(".core-pagination__icon")
        ?.classList.replace(
          "core-pagination__icon--state-disabled",
          "core-pagination__icon--state-enabled",
        );
      btn
        .querySelector(".core-pagination__link-label")
        ?.classList.replace(
          "core-pagination__link-label--disabled",
          "core-pagination__link-label--enabled",
        );
    }
  }

  private createPageHTML(item: PaginationItem): string {
    if (item === "...") {
      return `
        <li class="core-pagination__page core-pagination__page--disabled">
          <span class="core-pagination__ellipsis" aria-hidden="true">...</span>
        </li>
      `;
    }

    const isSelected = item === this.currentPage;

    return `
      <li class="core-pagination__page">
        <button
          class="core-pagination__page-button ${isSelected ? "core-pagination__page-button--selected" : ""}"
          type="button"
          aria-label="Página ${item}"
          aria-current="${isSelected ? "page" : "false"}"
          data-page="${item}"
        >
          <span class="core-pagination__page-number ${isSelected ? "core-pagination__page-number--selected" : ""}">
            ${item}
          </span>
        </button>
      </li>
    `;
  }

  private calculatePaginationItems(): PaginationItem[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const items: PaginationItem[] = [];

    let startPage = Math.max(1, current - 1);
    let endPage = Math.min(total, current + 1);

    if (current === 1) {
      endPage = Math.min(total, 3);
    }
    if (current === total) {
      startPage = Math.max(1, total - 2);
    }

    const rawSet = new Set<number>([1, total]);
    for (let i = startPage; i <= endPage; i++) {
      rawSet.add(i);
    }

    const sortedNumbers = Array.from(rawSet).sort((a, b) => a - b);

    for (let i = 0; i < sortedNumbers.length; i++) {
      const num = sortedNumbers[i];

      if (items.length > 0) {
        const lastAdded = items[items.length - 1] as number;

        const diff = num - lastAdded;

        if (diff === 2) {
          items.push(lastAdded + 1);
        } else if (diff > 2) {
          items.push("...");
        }
      }

      items.push(num);
    }

    return items;
  }
}

PluginRegistry.register("pagination", Pagination);
